import type {
  Concept,
  Reference,
  Shoot,
  Shot,
  Person,
  Location,
  LightingSetup,
  GearItem,
  Note,
  Tag,
  Backdrop,
  WardrobeItem,
  PropItem,
  SavedSearch,
  PresignRequest,
  PresignResponse,
} from '@photoshoot/shared'

/**
 * Typed fetch client for the Hono API.
 *
 * - Base URL from `VITE_API_URL`.
 * - `credentials: 'include'` so the httpOnly session cookie rides along.
 * - Parses the shared `ApiError` envelope on non-2xx and throws `ApiClientError`.
 * - WebAuthn passkey helpers (register / authenticate) drive the browser
 *   `navigator.credentials` API against `/auth/passkey/*`.
 */

const API_URL: string = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

// ----- error type -----

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: unknown
  }
}

// ----- M2 join payload types -----

/** A gear item joined with its per-shoot checklist state (GET /shoots/:id/gear). */
export interface ShootGearItem extends GearItem {
  checked: boolean
}

/** One entry sent on PUT /shoots/:id/gear (server owns the join table). */
export interface ShootGearEntry {
  gearItemId: string
  checked: boolean
}

export class ApiClientError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
    this.details = details
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error: unknown }).error === 'object'
  )
}

// ----- core request -----

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable body. */
  json?: unknown
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options

  const init: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...rest,
  }

  if (json !== undefined) {
    init.body = JSON.stringify(json)
  }

  let response: Response
  try {
    response = await fetch(`${API_URL}${path}`, init)
  } catch (cause) {
    throw new ApiClientError(
      0,
      'network_error',
      'Network request failed. Check your connection and try again.',
      cause,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  let parsed: unknown = undefined
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = text
    }
  }

  if (!response.ok) {
    if (isApiErrorBody(parsed)) {
      throw new ApiClientError(
        response.status,
        parsed.error.code,
        parsed.error.message,
        parsed.error.details,
      )
    }
    throw new ApiClientError(
      response.status,
      'http_error',
      `Request failed with status ${response.status}.`,
      parsed,
    )
  }

  return parsed as T
}

// ----- WebAuthn base64url helpers -----

function base64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const padLength = (4 - (padded.length % 4)) % 4
  const base64 = padded + '='.repeat(padLength)
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i] as number)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Map SimpleWebAuthn-style creation options (base64url strings) to the DOM type. */
function toCreationOptions(
  options: PublicKeyCredentialCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    excludeCredentials: options.excludeCredentials?.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  } as PublicKeyCredentialCreationOptions
}

/** Map SimpleWebAuthn-style request options (base64url strings) to the DOM type. */
function toRequestOptions(
  options: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: options.allowCredentials?.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  } as PublicKeyCredentialRequestOptions
}

// JSON shapes the server sends (base64url-encoded buffers). Minimal subset.
interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string
  rp: PublicKeyCredentialRpEntity
  user: { id: string; name: string; displayName: string }
  pubKeyCredParams: PublicKeyCredentialParameters[]
  timeout?: number
  attestation?: AttestationConveyancePreference
  authenticatorSelection?: AuthenticatorSelectionCriteria
  excludeCredentials?: Array<{ id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }>
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string
  timeout?: number
  rpId?: string
  userVerification?: UserVerificationRequirement
  allowCredentials?: Array<{ id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }>
}

/** Serialize a created/asserted credential back to base64url JSON for the server. */
function serializeAttestation(credential: PublicKeyCredential) {
  const response = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      transports:
        typeof response.getTransports === 'function' ? response.getTransports() : [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  }
}

function serializeAssertion(credential: PublicKeyCredential) {
  const response = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle:
        response.userHandle != null ? bufferToBase64url(response.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  }
}

// ----- passkey auth -----

export const auth = {
  /** True if the browser exposes the WebAuthn API. */
  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential === 'function' &&
      typeof navigator.credentials?.create === 'function'
    )
  },

  /** Enroll a new passkey: fetch options, create credential, verify with server. */
  async register(): Promise<{ verified: boolean }> {
    if (!this.isSupported()) {
      throw new ApiClientError(
        0,
        'webauthn_unsupported',
        'This browser does not support passkeys.',
      )
    }
    const options = await request<PublicKeyCredentialCreationOptionsJSON>(
      '/auth/passkey/register/options',
      { method: 'POST', json: {} },
    )
    const credential = (await navigator.credentials.create({
      publicKey: toCreationOptions(options),
    })) as PublicKeyCredential | null
    if (credential === null) {
      throw new ApiClientError(0, 'webauthn_cancelled', 'Passkey creation was cancelled.')
    }
    return request<{ verified: boolean }>('/auth/passkey/register/verify', {
      method: 'POST',
      json: serializeAttestation(credential),
    })
  },

  /** Authenticate with an existing passkey. */
  async login(): Promise<{ verified: boolean }> {
    if (!this.isSupported()) {
      throw new ApiClientError(
        0,
        'webauthn_unsupported',
        'This browser does not support passkeys.',
      )
    }
    const options = await request<PublicKeyCredentialRequestOptionsJSON>(
      '/auth/options',
      { method: 'POST', json: {} },
    )
    const credential = (await navigator.credentials.get({
      publicKey: toRequestOptions(options),
    })) as PublicKeyCredential | null
    if (credential === null) {
      throw new ApiClientError(0, 'webauthn_cancelled', 'Passkey sign-in was cancelled.')
    }
    return request<{ verified: boolean }>('/auth/verify', {
      method: 'POST',
      json: serializeAssertion(credential),
    })
  },

  /** Recovery-code fallback when no passkey is available. */
  async recover(code: string): Promise<{ verified: boolean }> {
    return request<{ verified: boolean }>('/auth/recovery/verify', {
      method: 'POST',
      json: { code },
    })
  },

  async logout(): Promise<void> {
    await request<void>('/auth/logout', { method: 'POST', json: {} })
  },

  /** Current session — resolves with the user id, rejects (401) if not signed in. */
  async me(): Promise<{ userId: string }> {
    return request<{ userId: string }>('/auth/me')
  },

  /** Whether a passkey has been enrolled yet (drives enroll-vs-sign-in UI). */
  async status(): Promise<{ enrolled: boolean }> {
    return request<{ enrolled: boolean }>('/auth/status')
  },
}

// ----- resource helpers -----

function crud<TEntity, TCreate>(resource: string) {
  return {
    list: () => request<TEntity[]>(`/${resource}`),
    get: (id: string) => request<TEntity>(`/${resource}/${id}`),
    create: (data: TCreate) =>
      request<TEntity>(`/${resource}`, { method: 'POST', json: data }),
    update: (id: string, data: Partial<TCreate>, ifUnmodifiedSince?: string) =>
      request<TEntity>(`/${resource}/${id}`, {
        method: 'PATCH',
        json: data,
        headers: ifUnmodifiedSince ? { 'If-Unmodified-Since': ifUnmodifiedSince } : undefined,
      }),
    remove: (id: string) =>
      request<void>(`/${resource}/${id}`, { method: 'DELETE' }),
  }
}

export const api = {
  request,
  auth,

  concepts: crud<Concept, Omit<Concept, 'id' | 'createdAt' | 'updatedAt'>>('concepts'),
  references: crud<Reference, Omit<Reference, 'id' | 'createdAt' | 'updatedAt'>>('references'),
  shoots: crud<Shoot, Omit<Shoot, 'id' | 'createdAt' | 'updatedAt'>>('shoots'),
  shots: crud<Shot, Omit<Shot, 'id' | 'createdAt' | 'updatedAt'>>('shots'),
  persons: crud<Person, Omit<Person, 'id' | 'createdAt' | 'updatedAt'>>('persons'),
  locations: crud<Location, Omit<Location, 'id' | 'createdAt' | 'updatedAt'>>('locations'),
  lightingSetups:
    crud<LightingSetup, Omit<LightingSetup, 'id' | 'createdAt' | 'updatedAt'>>('lightingSetups'),
  notes: crud<Note, Omit<Note, 'id' | 'createdAt'>>('notes'),

  // M4 (SPEC §14, Phase 2). Routes are added by the feature phases; these
  // typed clients match the standard REST shape (`crud`) so a feature can wire
  // its server route and consume this immediately.
  wardrobeItems:
    crud<WardrobeItem, Omit<WardrobeItem, 'id' | 'createdAt' | 'updatedAt'>>('wardrobeItems'),
  propItems:
    crud<PropItem, Omit<PropItem, 'id' | 'createdAt' | 'updatedAt'>>('propItems'),
  savedSearches:
    crud<SavedSearch, Omit<SavedSearch, 'id' | 'createdAt'>>('savedSearches'),

  /**
   * Gear catalog. `list()` returns everything; the filtered reads narrow by
   * provenance (`source`) or to a specific studio's on-site inventory
   * (`studioLocationId`) for the studio-aware checklist (SPEC §14).
   */
  gearItems: {
    list: () => request<GearItem[]>('/gearItems'),
    /** Gear filtered by source ('mine' | 'studio' | 'rented') and/or studio. */
    listBy: (params: { source?: string; studioLocationId?: string } = {}) => {
      const query = new URLSearchParams()
      if (params.source) query.set('source', params.source)
      if (params.studioLocationId) query.set('studioLocationId', params.studioLocationId)
      const qs = query.toString()
      return request<GearItem[]>(`/gearItems${qs ? `?${qs}` : ''}`)
    },
    /** A studio's on-site gear (source=studio, scoped to one Location). */
    forStudio: (studioLocationId: string) =>
      request<GearItem[]>(
        `/gearItems?source=studio&studioLocationId=${encodeURIComponent(studioLocationId)}`,
      ),
  },

  /**
   * Backdrops belong to a (studio) Location. The feature phase exposes them
   * under the location resource; the common read is "all backdrops for a
   * location" plus a full list.
   */
  backdrops: {
    list: () => request<Backdrop[]>('/backdrops'),
    forLocation: (locationId: string) =>
      request<Backdrop[]>(`/locations/${encodeURIComponent(locationId)}/backdrops`),
    create: (data: Omit<Backdrop, 'id' | 'createdAt'>) =>
      request<Backdrop>('/backdrops', { method: 'POST', json: data }),
    update: (id: string, data: Partial<Omit<Backdrop, 'id' | 'createdAt'>>) =>
      request<Backdrop>(`/backdrops/${encodeURIComponent(id)}`, { method: 'PATCH', json: data }),
    remove: (id: string) =>
      request<void>(`/backdrops/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },

  tags: {
    list: () => request<Tag[]>('/tags'),
  },

  /**
   * M2 join helpers — endpoints the generic `crud` does not cover. They call the
   * same `request` so the `ApiClientError` envelope + session cookie behave
   * identically. Server owns each join table; these PUTs replace the full set.
   */

  /** Shoot ⇄ concepts membership (PUT replaces the whole set). */
  shootConcepts: {
    list: (shootId: string) =>
      request<Concept[]>(`/shoots/${encodeURIComponent(shootId)}/concepts`),
    set: (shootId: string, conceptIds: ReadonlyArray<string>) =>
      request<{ ok: boolean; count: number }>(
        `/shoots/${encodeURIComponent(shootId)}/concepts`,
        { method: 'PUT', json: { conceptIds } },
      ),
  },

  /** Shoot gear checklist. GET returns each GearItem with its `checked` flag. */
  shootGear: {
    list: (shootId: string) =>
      request<ShootGearItem[]>(`/shoots/${encodeURIComponent(shootId)}/gear`),
    set: (shootId: string, gear: ReadonlyArray<ShootGearEntry>) =>
      request<{ ok: boolean; count: number }>(
        `/shoots/${encodeURIComponent(shootId)}/gear`,
        { method: 'PUT', json: { gear } },
      ),
  },

  /** Shot ⇄ key-references membership (PUT replaces the whole set). */
  shotReferences: {
    list: (shotId: string) =>
      request<Reference[]>(`/shots/${encodeURIComponent(shotId)}/references`),
    set: (shotId: string, referenceIds: ReadonlyArray<string>) =>
      request<{ ok: boolean; count: number }>(
        `/shots/${encodeURIComponent(shotId)}/references`,
        { method: 'PUT', json: { referenceIds } },
      ),
  },

  /** Shots scoped to a shoot — the common case (a shoot's ordered shot list). */
  shotsForShoot: (shootId: string) =>
    request<Shot[]>(`/shots?shootId=${encodeURIComponent(shootId)}`),

  /**
   * Reorder a shoot's shots. The API has no bulk `/reorder` endpoint — ordering
   * lives in each shot's `sortOrder`, so this PATCHes each changed shot in order.
   * Pass the shots in their desired sequence; index becomes `sortOrder`.
   *
   * The server PATCH path enforces optimistic concurrency (`If-Unmodified-Since`
   * required → 428 when absent), so each shot's `updatedAt` is sent as the
   * precondition. Without it every reorder fails on the first iteration.
   * Returns the updated shots in their new order.
   */
  reorderShots: async (
    orderedShots: ReadonlyArray<Pick<Shot, 'id' | 'updatedAt'>>,
  ): Promise<Shot[]> => {
    const updated: Shot[] = []
    for (let index = 0; index < orderedShots.length; index += 1) {
      const shot = orderedShots[index] as Pick<Shot, 'id' | 'updatedAt'>
      updated.push(
        await request<Shot>(`/shots/${encodeURIComponent(shot.id)}`, {
          method: 'PATCH',
          json: { sortOrder: index },
          headers: { 'If-Unmodified-Since': shot.updatedAt },
        }),
      )
    }
    return updated
  },

  /** Image URLs are auth-gated 302s — build the URL, the cookie does the rest. */
  references_fileUrl: (id: string) => `${API_URL}/references/${id}/file`,
  references_thumbUrl: (id: string) => `${API_URL}/references/${id}/thumb`,

  /** Search: full-text + tag/namespace/kind filters. */
  search: (params: { q?: string; ns?: string; tag?: string; kind?: string }) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, value)
    }
    return request<Reference[]>(`/search?${query.toString()}`)
  },

  /** Presigned R2 upload. */
  presign: (body: PresignRequest) =>
    request<PresignResponse>('/uploads/presign', { method: 'POST', json: body }),
}

export type Api = typeof api
